import React, { useEffect, useState } from "react";
import PageLoader from "../loader/PageLoader";
import {
    ArrowLeft,
    ChatText,
    CurrencyInr,
    Pencil,
    Trash,
    WarningCircle,
    X,
} from "@phosphor-icons/react";
import axiosInstance from "../../api/axiosInstance";
import dayjs from "dayjs";
import { RejectHotel } from "./RejectAPI";
import DragDrop from "../DragDrop";
import ImageLoader from "../loader/ImageLoader";
import { CompleteHotel } from "./CompleteAPI";
import { formatHotel } from "../../api/hotelTime";
import { uploadImageToServer } from "../../api/uploadImage";

const Checkout = ({ func, selectedRequest }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [remarks, setRemarks] = useState("");
    const [newRooms, setNewRooms] = useState([]);
    const [amount, setAmount] = useState("");
    const [openUploadBill, setOpenUploadBill] = useState(false);
    const [media, setMedia] = useState([]);
    const [processedBills, setProcessedBills] = useState([]);
    const [selectedBill, setSelectedBill] = useState(null);
    const [isModified, setIsModified] = useState(false);
    const [selectedBillAmount, setSelectedBillAmount] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get(
                    `/api/v1/hotels/${selectedRequest.hotelId}/bookings/${selectedRequest.bookingId}/booked-rooms/${selectedRequest.bookedRoomId}/guest-requests/post-checkin/checkout-request`,
                    { params: { guest_request_id: selectedRequest.requestId } },
                );
                const processed = response.data.bills.map((bill) => ({
                    guest_request_id: bill.guest_request_id,
                    totalAmount: bill.total || null,
                    bills: bill.bills || [],
                    type: bill.type,
                    room_number: bill.room_number,
                    created_at: bill.created_at,
                }));
                setProcessedBills(processed);
                
                let price = (response.data && response.data.total_amount!=null && response.data.total_amount!=undefined) ? response.data.total_amount.toString() : "";
                setAmount(price);


                setData(response.data);
                setRemarks(response.data?.remarks || "");
                setLoading(false);
            } catch (err) {
                setErrors({ fetch: "Failed to fetch Extend Room data" });
                setLoading(false);
            }
        };
        fetchData();
    }, [selectedRequest]);

    useEffect(() => {
        if (selectedBill) {
            setSelectedBillAmount(selectedBill.totalAmount || "");
            // Instead of directly mapping bills to media, fetch the images
            if (selectedBill.bills && selectedBill.bills.length > 0) {
                fetchFloorPlanImages(selectedBill.bills);
            } else {
                setMedia([]); // Reset media if no bills
            }
        }
    }, [selectedBill]);

    const handleBillSave = () => {
        setProcessedBills((prev) =>
            prev.map((bill) =>
                bill.guest_request_id === selectedBill.guest_request_id
                    ? {
                          ...bill,
                          totalAmount: selectedBillAmount || null,
                          bills: media.map((m) => m.key),
                      }
                    : bill,
            ),
        );
        setOpenUploadBill(false);
        setSelectedBill(null);
        setMedia([]);
        setSelectedBillAmount("");
    };

    // Reject Request
    const handleRejectRequest = async () => {
        try {
            const request = {
                hotelId: selectedRequest.hotelId,
                bookingId: selectedRequest.bookingId,
                guestRequestId: data?.guest_request_id,
            };
            await RejectHotel(request);
            func(false);
        } catch (error) {
            setErrors({ reject: "Failed to reject the Extend room request" });
            console.error(error);
        }
    };

    // Complete Request
    // const handleCompleteRequest = async () => {
    //     try {
    //         const request = {
    //             hotelId: selectedRequest.hotelId,
    //             bookingId: selectedRequest.bookingId,
    //             guestRequestId: data?.guest_request_id,
    //         };
    //         await CompleteHotel(request);
    //         func(false);
    //     } catch (error) {
    //         setErrors({ reject: "Failed to complete the Extend room request" });
    //         console.error(error);
    //     }
    // };

    const handleFileUpload = async (files) => {
        const fileArray = Array.from(files);
        const newMediaEntries = fileArray.map((file) => ({
            key: file.name,
            url: URL.createObjectURL(file),
            loading: true,
            error: false,
        }));
        setMedia((prevMedia) => [...prevMedia, ...newMediaEntries]);

        const uploadPromises = newMediaEntries.map(async (entry, index) => {
            try {
                const uploadUrlResponse = await axiosInstance.get(
                    `/api/v1/images/upload-presigned-url?file_name=${entry.key}`,
                );
                const { key, url } = uploadUrlResponse.data;
                const isSuccess = await uploadImageToServer(
                    fileArray[index],
                    url,
                );

                setMedia((prevMedia) =>
                    prevMedia.map((mediaEntry) =>
                        mediaEntry.key === entry.key
                            ? {
                                  ...mediaEntry,
                                  key: key, // Replace with actual key
                                  loading: false,
                                  error: !isSuccess,
                              }
                            : mediaEntry,
                    ),
                );
                return isSuccess;
            } catch (error) {
                console.error("Error uploading image:", error);
                setMedia((prevMedia) =>
                    prevMedia.map((mediaEntry) =>
                        mediaEntry.key === entry.key
                            ? { ...mediaEntry, loading: false, error: true }
                            : mediaEntry,
                    ),
                );
                return false;
            }
        });

        await Promise.all(uploadPromises);
    };

    const fetchFloorPlanImages = async (imageKeys = []) => {
        // Set initial loading state for all images
        setMedia(
            imageKeys.map((key) => ({
                key,
                url: "",
                loading: true,
                error: false,
            })),
        );

        try {
            const imagePromises = imageKeys.map(async (key) => {
                try {
                    const response = await axiosInstance.get(
                        `/api/v1/images/download-presigned-url?key=${key}`,
                    );
                    const imageUrl = response.data.url;
                    return { key, url: imageUrl, loading: false, error: false };
                } catch (error) {
                    console.error(
                        "Error fetching image URL:",
                        error,
                        "for key:",
                        key,
                    );
                    return { key, url: "", loading: false, error: true };
                }
            });

            const images = await Promise.all(imagePromises);
            setMedia(images);
        } catch (error) {
            console.error("Error in fetchFloorPlanImages:", error);
            setMedia(
                imageKeys.map((key) => ({
                    key,
                    url: "",
                    loading: false,
                    error: true,
                })),
            );
        }
    };

    const removeImage = (indexToRemove) => {
        setMedia((prevMedia) =>
            prevMedia.filter((_, index) => index !== indexToRemove),
        );
    };

    const trimImgName = (name) => {
        if (name && name.length > 12) {
            return name.substring(0, 12) + "...";
        }
        return name || "";
    };

    // Respond to Guest
    const handleRespondToGuest = async () => {
        // Include all bills in the update request
        const updateData = {
            remarks: remarks,
            total_amount: amount ? parseFloat(amount) : null,
            bills: processedBills.map((bill) => ({
                guest_request_id: bill.guest_request_id,
                bills: bill.bills,
                total: bill.totalAmount ? parseFloat(bill.totalAmount) : null,
            })),
        };

        // console.log("Payload : ",updateData)
        // return;

        // console.log(updateData);
        try {
            await axiosInstance.put(
                `/api/v1/hotels/${selectedRequest.hotelId}/bookings/${selectedRequest.bookingId}/booked-rooms/${selectedRequest.bookedRoomId}/guest-requests/post-checkin/checkout-request/${data?.id}`,
                updateData,
            );
            func(false);
        } catch (error) {
            setErrors({ fetch: "Failed to update Extend Room request" });
            console.error(error);
        }
    };

    // Complete Request New
    const handleCompleteRequest = async () => {
        // Include all bills in the update request
        const updateData = {
            payment_status : "SUCCESSFUL",
            remarks: remarks,
            total_amount: amount ? parseFloat(amount) : null,
            bills: processedBills.map((bill) => ({
                guest_request_id: bill.guest_request_id,
                bills: bill.bills,
                total: bill.totalAmount ? parseFloat(bill.totalAmount) : null,
            })),
        };

        // console.log(updateData);
        try {
            await axiosInstance.put(
                `/api/v1/hotels/${selectedRequest.hotelId}/bookings/${selectedRequest.bookingId}/booked-rooms/${selectedRequest.bookedRoomId}/guest-requests/post-checkin/checkout-request/${data?.id}`,
                updateData,
            );
            func(false);
        } catch (error) {
            setErrors({ fetch: "Failed to update Extend Room request" });
            console.error(error);
        }
    };


    const handleAmountUpdate = (billId, newAmount) => {
        setProcessedBills((prev) =>
            prev.map((bill) =>
                bill.guest_request_id === billId
                    ? { ...bill, totalAmount: newAmount, isModified: true }
                    : bill,
            ),
        );
        setIsModified(true);
    };

    const renderBillItem = (bill) => {
        const isPaid = bill.bills.length > 0 || bill.totalAmount !== null;
        const formattedDate = formatHotel(bill.created_at * 1000, "DD MMM YYYY HH:mm A");

        return (
            <div className="border-[1.5px] rounded-md p-1.5 border-gray-300 flex flex-col gap-1">
                <p className="text-[#0A0D0C] font-semibold text-[16px]">
                    {bill.type}
                </p>
                <p className="text-[16px]">Room {bill.room_number}</p>
                <p>{formattedDate}</p>
                {isPaid ? (
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {bill.totalAmount && (
                                <span>₹{bill.totalAmount}</span>
                            )}
                            {bill.bills.length > 0 && (
                                <span className="text-sm text-gray-500">
                                    ({bill.bills.length}{" "}
                                    {bill.bills.length === 1 ? "bill" : "bills"}
                                    )
                                </span>
                            )}
                        </div>
                        <button
                            className="bg-[#0b22b2] p-1.5 rounded-sm outline-none border-none text-white"
                            variant="outline"
                            size="icon"
                            onClick={() => {
                                setSelectedBill(bill);
                                setOpenUploadBill(true);
                            }}
                        >
                            <Pencil className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <button
                        className="bg-[#0b22b2] p-1.5 rounded-sm outline-none border-none text-white"
                        variant="secondary"
                        onClick={() => {
                            setSelectedBill(bill);
                            setOpenUploadBill(true);
                        }}
                    >
                        Upload Bill
                    </button>
                )}
                {/* <div className="flex justify-between items-center">
                    <p>Total : ₹800</p>
                    <button className="bg-[#0b22b2] p-1.5 rounded-sm outline-none border-none text-white">
                        <Pencil weight="bold" />
                    </button>
                </div> */}
            </div>
        );
    };

    console.log("Processed Billss : ",processedBills);

    useEffect(() => {
        const totalAmount = () => {
            try {
                // Check if processedBills is defined, not null, and not an empty array
                if (Array.isArray(processedBills) && processedBills.length > 0) {

                    // Sum up totalAmount from each object in the array
                    const total = processedBills.reduce((acc, curr) => {
                        console.log(curr.totalAmount)
                        return acc + parseFloat(curr.totalAmount || 0);
                    }, 0);
                    
                    // Call setAmount with the computed total
                    setAmount(total);
                } else {
                    // If processedBills is invalid, optionally reset the amount
                    setAmount(0);
                }
            } catch (error) {
                console.log('Error calculating total amount:', error);
            }
        };

        totalAmount();
    }, [processedBills]);

    return (
        <div className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-60 flex items-center justify-center z-[1001]">
            {loading && <PageLoader />}
            {openUploadBill ? (
                <form
                    className="popup-content bg-white p-6 rounded-lg text-sm flex flex-col gap-2 w-[80%] md:w-[70%] lg:w-[50%]"
                    onSubmit={(e) => e.preventDefault()}
                >

                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => {
                                setOpenUploadBill(false);
                                setSelectedBill(null);
                                setMedia([]);
                                setSelectedBillAmount("");
                            }}
                            className="hover:bg-[#F2F7FF] p-1.5 border-[1.5px] rounded-md"
                        >
                            <ArrowLeft weight="bold" size={18} />
                        </button>
                        <h2 className="text-xl font-semibold">Upload Bill</h2>
                        <button
                            onClick={() => func(false)}
                            className="hover:bg-[#F2F7FF] p-1.5 border-[1.5px] rounded-md"
                            type="button"
                        >
                            <X weight="bold" size={18} />
                        </button>
                    </div>


                    <div className="w-full flex">
                        <div className="w-full mb-6 flex">
                            <div className="w-[70%]">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <div className="w-1/3">
                                        <p className="text-[#8C9199] mb-2">
                                            Request ID
                                        </p>
                                        <p className="pr-4">
                                            {data?.room_numbers.map((d) => d)}
                                        </p>
                                    </div>
                                </div>
                                <div className="my-1">
                                    <p className="text-[#8C9199] mb-1">
                                        Bill Total
                                    </p>
                                    <div className="border-[1.5px] rounded-md p-1.5 w-2/3 flex items-center gap-2">
                                        <CurrencyInr />
                                        <input
                                            type="text"
                                            placeholder="Enter bill amount"
                                            className="outline-none border-none w-full"
                                            value={selectedBillAmount}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (
                                                    !isNaN(value) &&
                                                    value >= 0
                                                ) {
                                                    setSelectedBillAmount(
                                                        value,
                                                    );
                                                }
                                            }}
                                            disabled={data?.status=="InProgress_Hotel" || data?.status==="Scheduled_Hotel"}
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center justify-center">
                                    <DragDrop
                                        onFilesUploaded={handleFileUpload}
                                        acceptedFileTypes={{
                                            "image/*": [
                                                ".jpeg",
                                                ".png",
                                                ".jpg",
                                            ],
                                        }}
                                        maxFiles={10}
                                        dragDropText="Drag and drop images here"
                                    />
                                </div>
                            </div>
                            <div className="w-[30%] bg-[#F2F7FF] h-[40vh] overflow-y-auto">
                                {media.map((file, index) => (
                                    <div
                                        key={index}
                                        className="w-full h-full rounded-lg border-[1.5px] border-[#BFC4CC] overflow-hidden"
                                    >
                                        {file.loading ? (
                                            <ImageLoader
                                                width="100%"
                                                height="142px"
                                            />
                                        ) : file.error ? (
                                            <div className="w-full h-[142px] flex items-center justify-center bg-gray-100 text-red-500">
                                                Failed to load image
                                            </div>
                                        ) : (
                                            <>
                                                <img
                                                    src={file.url}
                                                    alt={file.key}
                                                    className="w-full bg-cover h-full object-cover"
                                                    onError={(e) => {
                                                        setMedia((prevMedia) =>
                                                            prevMedia.map(
                                                                (item, i) =>
                                                                    i === index
                                                                        ? {
                                                                              ...item,
                                                                              error: true,
                                                                          }
                                                                        : item,
                                                            ),
                                                        );
                                                    }}
                                                />
                                                <div className="flex justify-between items-center h-[32px] px-1 bg-[#EEF0FB]">
                                                    <p>
                                                        {trimImgName(file.key)}
                                                    </p>
                                                    <button
                                                        className="bg-white rounded-md p-1 border-[1.5px] border-[#F8D9DE] text-[#DC4662] h-[24px] w-[24px]"
                                                        onClick={() =>
                                                            removeImage(index)
                                                        }
                                                    >
                                                        <Trash size={14} />
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <button

                        className="p-2 w-full bg-[#ff7378] rounded-md font-medium hover:bg-[#f75f64] transition duration-150 outline-none"
                        type="button"
                        onClick={handleBillSave}
                    >
                        Save Bill
                    </button>
                </form>
            ) : (
                <form
                    className="popup-content bg-white p-6 rounded-lg text-sm flex flex-col gap-2 w-[80%] md:w-[70%] lg:w-[50%]"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold">Checkout</h2>
                        <button
                            onClick={() => func(false)}
                            className="hover:bg-[#F2F7FF] p-1.5 border-[1.5px] rounded-md"
                            type="button"
                        >
                            <X weight="bold" size={18} />
                        </button>
                    </div>
                    <div className="w-full flex">
                        <div className="w-2/3 pr-2 flex flex-col gap-3">
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="w-1/3">
                                    <p className="text-[#8C9199] mb-2">
                                        Room Number
                                    </p>
                                    <p className="pr-4">
                                        {data?.room_numbers.map((d) => d)}
                                    </p>
                                </div>
                                <div className="w-1/3">
                                    <p className="text-[#8C9199] mb-2">
                                        Total requests
                                    </p>
                                    <p className="pr-4">{data?.bills.length}</p>
                                </div>
                                <div className="w-1/3">
                                    <p className="text-[#8C9199] mb-2">
                                        Bills Uploaded
                                    </p>
                                    <p className="pr-4">
                                        {
                                            processedBills.filter(
                                                (bill) => bill.bills.length > 0,
                                            ).length
                                        }
                                    </p>
                                </div>
                            </div>
                            <div className="">
                                <p className="mb-1 text-[#8C9199]">
                                    Total Amount
                                </p>
                                <div className="border-[1.5px] rounded-md p-1.5 w-full flex items-center gap-2">
                                    <CurrencyInr />
                                    <input
                                        type="text"
                                        placeholder="Enter your fee"
                                        className="outline-none border-none w-full"
                                        value={amount}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (!isNaN(value) && value >= 0) {
                                                setAmount(value);
                                            }
                                        }}
                                        disabled={data?.status=="InProgress_Hotel" || data?.status==="Scheduled_Hotel"}
                                    />
                                </div>
                            </div>{" "}
                            <div>
                                <p className="text-[#8C9199] mb-1">Remarks</p>
                                <textarea
                                    rows={4}
                                    className="w-full border border-gray-300 rounded-md p-2 mt-1 focus:outline-none focus:border-blue-400"
                                    placeholder="Mention your comment here"
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                ></textarea>
                            </div>
                        </div>
                        <div className="w-1/3 bg-[#EEF0FB] p-2 flex flex-col gap-1 h-[40vh] overflow-scroll">
                            <style jsx="true">{`
                                div::-webkit-scrollbar {
                                    display: none; /* For Chrome, Safari, and Opera */
                                }
                            `}</style>
                            {processedBills.map(renderBillItem)}
                        </div>
                    </div>
                    {errors.staff && (
                        <p className="text-[#ff7378] text-[12px] flex items-center gap-1 p-0.5 -mt-2">
                            <WarningCircle />
                            {errors.staff}
                        </p>
                    )}

                    {/* Buttons */}
                    <div className="flex gap-3 mt-2 w-full">
                        <div className="flex gap-2 w-2/3">
                            <button
                                className="p-2 w-full bg-[#ff7378] rounded-md font-medium hover:bg-[#f75f64] transition duration-150 outline-none"
                                type="button"
                                onClick={handleRespondToGuest}
                            >
                                Respond To Guest
                            </button>
                            <button
                                className="p-2 w-full border-[1.5px] bg-[#73c9ff] rounded-md font-medium hover:bg-[#5fa4f7] transition duration-150 outline-none"
                                type="button"
                                onClick={handleCompleteRequest}
                            >
                                Complete Request
                            </button>
                        </div>
                        <button
                            className="p-2 w-1/3 border-[1.5px] border-red-300 text-[#ff7378] rounded-md font-medium hover:bg-red-50 transition duration-150 outline-none"
                            type="button"
                            onClick={handleRejectRequest}
                        >
                            Reject Request
                        </button>
                    </div>

                    {errors.fetch && (
                        <p className="text-red-500">{errors.fetch}</p>
                    )}
                    {errors.reject && (
                        <p className="text-red-500">{errors.reject}</p>
                    )}
                </form>
            )}
        </div>
    );
};

export default Checkout;
